import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { WbsItemSearchConditionV2VO } from '../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import WbsItemApi, {
  createWbsItemDeltaInputByRow,
  isWatcherEdited,
  WbsItemBatchDeltaRequest,
  WbsItemDeltaInput,
  WbsItemRow,
  WbsItemUpdateBatchDeltaRequest,
} from '../../../../lib/functions/wbsItem'
import {
  WbsItemIndex,
  WbsItemIndexUser,
  searchWbsItemV2,
  countSearchWbsItemV2,
} from '../../../../lib/functions/wbsItemV2'
import { useWbsItemSearchConditionV2ApiRequestTransformService } from '../../../../services/transform-service/wbsItemSearchConditionV2ApiRequestTransformService'
import store from '../../../../store'
import { doNotRequireSave } from '../../../../store/requiredSaveData'
import { WbsItemSearchRow } from '../wbsItemSearch'
import { ProjectMemberProps } from '../../../../lib/functions/projectMember'
import { isTagColumnEdited } from '../../../../lib/functions/tag'
import { successDummyResponse } from '../../../../lib/commons/api'
import {
  UpdateSprintProductItemProps,
  updateSprintProductItems,
} from '../../../../lib/functions/sprintProductItem'
import SprintBacklogApi, {
  UpdateBatchSprintBacklogProps,
} from '../../../../lib/functions/sprintBacklog'
import { syncSprintFromSprints } from '../../../../lib/functions/sprint'
import { addScreenMessage, MessageLevel } from '../../../../store/messages'
import { intl } from '../../../../i18n'
import { featureFlagService } from '../../../../utils/feature-flag'
import { formatDateTime } from '../../../../utils/date'
import { UserBasic } from '../../../../lib/functions/user'
import { CUSTOM_ENUM_NONE } from '../../../../lib/commons/customEnum'
import Workload from '../../../../lib/functions/workload'
import DateVO from '../../../../vo/DateVO'
import { wbsItemAdditionalPropertyValuesVoService } from '../../../../domain/value-object/WbsItemAdditionalPropertyValuesVO'
import { WbsItemTypeVO } from '../../../../domain/value-object/WbsItemTypeVO'
import { getTypeIndex } from '../../../containers/commons/AgGrid/components/cell/custom/wbsItemType'

const FETCH_LIMIT: number = 10000

const getTicketTypeIndex = (wbsItemType: WbsItemTypeVO): number => {
  let type = wbsItemType
  while (type.child) {
    type = type.child
  }
  return getTypeIndex(type)
}

const toProjectMemberProps = (
  user?: WbsItemIndexUser
): ProjectMemberProps | undefined => {
  if (!user) return undefined
  return {
    uuid: user.uuid,
    name: user.name ?? '',
    iconUrl: '',
  }
}

const toUserBasic = (user?: WbsItemIndexUser): UserBasic | undefined => {
  if (!user) return undefined
  return {
    uuid: user.uuid,
    code: user.code ?? '',
    iconUrl: '',
    name: user.name ?? '',
  }
}

const createRowByWbsItemIndex = (item: WbsItemIndex): WbsItemSearchRow => {
  const { dailyWorkHours, monthlyWorkDays } =
    store.getState().tenant.organization!
  const baseWbsItemType = store.getState().project.wbsItemTypes.get(item.type)
  const wbsItemType = item.typeDto
    ? new WbsItemTypeVO(item.typeDto)
    : baseWbsItemType
  const wbsItemRow: WbsItemRow = {
    uuid: item.uuid,
    projectUuid: item.projectUuid,
    lockVersion: item.lockVersion,
    code: item.code,
    type: item.type,
    wbsItemType: wbsItemType,
    baseWbsItemType,
    displayName: item.displayName,
    description: item.description,
    team: item.team
      ? {
          uuid: item.team.uuid,
          iconUrl: '',
          projectUuid: '',
          displayName: item.team.displayName ?? '',
          officialName: item.team.officialName ?? '',
        }
      : undefined,
    accountable: toProjectMemberProps(item.accountable),
    responsible: toProjectMemberProps(item.responsible),
    assignee: toProjectMemberProps(item.assignee),
    closedBy: toProjectMemberProps(item.closedBy),
    revision: item.revision,
    createdAt: formatDateTime(item.createdAt),
    createdBy: toUserBasic(item.createdBy),
    updatedAt: formatDateTime(item.updatedAt),
    updatedBy: toUserBasic(item.updatedBy),
    critical: item.critical,
    deadline: item.deadline ? new DateVO(item.deadline).format() : '',
    difficulty:
      item.difficulty === CUSTOM_ENUM_NONE ? undefined : item.difficulty,
    estimatedStoryPoint: item.estimatedStoryPoint,
    estimatedWorkload: Workload.from({
      hour: item.estimatedHour,
      standard: { dailyWorkHours, monthlyWorkDays },
    }),
    actualHour: item.actualHour,
    sprint: item.sprints?.[0],
    sprints: item.sprints ?? [],
    priority: item.priority === CUSTOM_ENUM_NONE ? undefined : item.priority,
    scheduledDate: {
      startDate: item.scheduledStartDate,
      endDate: item.scheduledEndDate,
    },
    actualDate: {
      startDate: item.actualStartDate,
      endDate: item.actualEndDate,
    },
    status: item.status
      ? (item.status as import('../../../../domain/entity/WbsItemEntity').WbsItemStatus)
      : undefined,
    substatus: item.substatus,
    watchers: item.watchers
      ? item.watchers.map(w => toProjectMemberProps(w)!).filter(Boolean)
      : [],
    ticketType: item.ticketType,
    tags: item.tags,
    additionalPropertyValues: item.additionalPropertyValues
      ? wbsItemAdditionalPropertyValuesVoService.of(
          item.additionalPropertyValues
        )
      : undefined,
  }

  return {
    uuid: item.uuid,
    lockVersion: item.lockVersion,
    wbsItem: wbsItemRow,
    cumulation: item.cumulation,
    path: item.path,
    typeIndex: wbsItemType ? getTypeIndex(wbsItemType) : undefined,
    ticketTypeIndex: wbsItemType ? getTicketTypeIndex(wbsItemType) : undefined,
    parentWbsItem: item.parentWbsItem
      ? {
          uuid: item.parentWbsItem.uuid,
          displayName: item.parentWbsItem.displayName,
          wbsItemType: item.parentWbsItem.type
            ? store.getState().project.wbsItemTypes.get(item.parentWbsItem.type)
            : undefined,
        }
      : undefined,
    revision: item.revision,
    createdAt: wbsItemRow.createdAt
      ? new Date(item.createdAt).getTime()
      : undefined,
    createdBy: toUserBasic(item.createdBy),
    updatedAt: wbsItemRow.updatedAt
      ? new Date(item.updatedAt).getTime()
      : undefined,
    updatedBy: toUserBasic(item.updatedBy),
    commentSummary: item.commentSummary,
    deliverableAttachmentSummary: item.deliverableAttachmentSummary,
    treeValue: [item.uuid],
  } as WbsItemSearchRow
}

export interface Props {
  applicationFunctionUuid: string
  projectUuid: string
  searchCondition: WbsItemSearchConditionV2VO
}

export const useWbsItemSearchDataV2 = ({
  applicationFunctionUuid,
  projectUuid,
  searchCondition,
}: Props) => {
  const [totalDataSize, setTotalDataSize] = useState<number>(0)
  const [hitDataSize, setHitDataSize] = useState<number>(0)
  const [count, setCount] = useState<number>(0)
  const [data, setDataInternal] = useState<WbsItemSearchRow[]>([])
  const originalData = useRef<Map<string, WbsItemSearchRow>>(new Map())

  const searchConditionApiRequestTransformer =
    useWbsItemSearchConditionV2ApiRequestTransformService(projectUuid)

  const searchConditionForApiV2 = useMemo(
    () =>
      searchConditionApiRequestTransformer.toV2ApiRequest(
        searchCondition,
        0,
        FETCH_LIMIT
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [projectUuid, searchCondition]
  )

  useEffect(() => {
    async function fetchData() {
      const countResponse = await countSearchWbsItemV2(searchConditionForApiV2)
      setCount(countResponse.json)
    }
    fetchData()
  }, [searchConditionForApiV2])

  const fetch = useCallback(async () => {
    const response = await searchWbsItemV2(searchConditionForApiV2)
    const items: WbsItemIndex[] = response.json
    setTotalDataSize(items.length)
    setHitDataSize(items.length)

    if (items.length >= FETCH_LIMIT) {
      store.dispatch(
        addScreenMessage(applicationFunctionUuid, {
          type: MessageLevel.WARN,
          title: intl.formatMessage({
            id: 'bulksheet.warning.search.limit',
          }),
          text: intl.formatMessage({
            id: 'bulksheet.warning.search.limit.detail',
          }),
        })
      )
    }

    return items
      .slice(0, FETCH_LIMIT)
      .map(item => createRowByWbsItemIndex(item))
  }, [searchConditionForApiV2, applicationFunctionUuid])

  const refresh = useCallback(async () => {
    const rows: WbsItemSearchRow[] | undefined = await fetch()
    if (!rows) return
    setDataInternal(rows)
    originalData.current.clear()
    rows
      .filter(row => !!row.wbsItem)
      .forEach(row => {
        originalData.current.set(row.uuid, _.cloneDeep(row))
      })
    store.dispatch(doNotRequireSave())
  }, [fetch])

  useEffect(() => {
    refresh()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshSingleRow = useCallback(
    async (wbsItemUuid: string | undefined) => {
      if (!wbsItemUuid) return
      const targetData: WbsItemSearchRow | undefined = data.find(
        d => d?.wbsItem?.uuid === wbsItemUuid
      )
      if (!targetData) return
      // V2 uses the same findByUuid for single row refresh
      const response = await WbsItemApi.findByUuid(wbsItemUuid)
      const responseItem: WbsItemIndex = response.json
      const newRow = createRowByWbsItemIndex(responseItem)
      const newData: WbsItemSearchRow[] = data.map((row: WbsItemSearchRow) => {
        if (row.uuid !== newRow.uuid) return row
        return newRow
      })
      originalData.current.set(newRow.uuid, _.cloneDeep(newRow))
      setDataInternal(newData)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  )

  // Save ----------
  const rowToWbsItemDeltaInput = useCallback(
    (
      row: WbsItemSearchRow | undefined,
      originalDataMap: Map<string, WbsItemSearchRow>
    ) => {
      if (!row) return undefined
      const original: WbsItemSearchRow | undefined = originalDataMap.get(
        row.uuid
      )
      const originalWbsItem: WbsItemRow | undefined = original?.wbsItem
      const wbsItem: WbsItemRow = row.wbsItem

      return createWbsItemDeltaInputByRow(originalWbsItem, wbsItem)
    },
    []
  )

  const updateWbsItem = useCallback(
    async (
      projectUuid: string,
      data: WbsItemSearchRow[],
      originalDataMap: Map<string, WbsItemSearchRow>
    ) => {
      const editedRows = data.filter(
        (row: WbsItemSearchRow) => row.edited && originalDataMap.has(row.uuid)
      )

      const edited = editedRows
        .map((row: WbsItemSearchRow) =>
          rowToWbsItemDeltaInput(row, originalDataMap)
        )
        .filter(v => !!v)

      const watchers = editedRows
        .filter((row: WbsItemSearchRow) => {
          const original =
            originalDataMap.get(row.uuid)?.wbsItem?.watchers ?? []
          const watchers = row.wbsItem?.watchers ?? []
          return isWatcherEdited(original, watchers)
        })
        .map((row: WbsItemSearchRow) => ({
          wbsItemUuid: row.wbsItem?.uuid,
          userUuids: row.wbsItem?.watchers?.map(watcher => watcher.uuid),
        }))

      const tags = editedRows
        .filter((row: WbsItemSearchRow) => {
          const original = originalDataMap.get(row.uuid)?.wbsItem?.tags ?? []
          const watchers = row.wbsItem?.tags ?? []
          return isTagColumnEdited(original, watchers)
        })
        .map((row: WbsItemSearchRow) => ({
          wbsItemUuid: row.wbsItem?.uuid,
          tagUuids: row.wbsItem?.tags?.map(tag => tag.uuid),
        }))

      const response = await WbsItemApi.updateBatchDelta({
        wbsItems: {
          added: [],
          edited,
          deleted: [],
        } as WbsItemUpdateBatchDeltaRequest,
        watchers,
        tags,
      } as WbsItemBatchDeltaRequest)

      return response
    },
    [rowToWbsItemDeltaInput]
  )

  const updateSprintProduct = useCallback(
    async (
      data: WbsItemSearchRow[],
      originalDataMap: Map<string, WbsItemSearchRow>,
      wbsItemLockVersionMap: { [wbsItemUuid: string]: number }
    ) => {
      const useSprintsMultiSelect = featureFlagService.isEnabled(
        'sprints-multi-select'
      )
      const deliverables = data.filter(
        (row: WbsItemSearchRow) =>
          row.edited &&
          !!row.wbsItem?.uuid &&
          row.wbsItem?.wbsItemType?.isDeliverable()
      )
      if (_.isEmpty(deliverables)) return successDummyResponse
      const sprintProductRequest: UpdateSprintProductItemProps = {
        added: [],
        edited: [],
        deleted: [],
      }
      deliverables.forEach((row: WbsItemSearchRow) => {
        const wbsItem = row.wbsItem
        if (!wbsItem?.uuid) return
        if (!row.added && !row.edited) return

        const originalSprints = originalData.current.get(row.uuid)?.wbsItem
          .sprints
        const deliverableUuid = wbsItem.uuid
        const deliverableLockVersion =
          wbsItemLockVersionMap[wbsItem.uuid] ?? wbsItem.lockVersion!

        if (useSprintsMultiSelect) {
          const currentSprints = wbsItem.sprints ?? []
          const currentSprintUuids = new Set(currentSprints.map(s => s.uuid))
          const originalSprintUuids = new Set(
            originalSprints?.map(s => s.uuid) ?? []
          )

          currentSprints.forEach(sprint => {
            if (!originalSprintUuids.has(sprint.uuid)) {
              sprintProductRequest.added.push({
                deliverableUuid,
                deliverableLockVersion,
                sprintUuid: sprint.uuid,
              })
            }
          })

          originalSprints?.forEach(sprint => {
            if (!currentSprintUuids.has(sprint.uuid)) {
              sprintProductRequest.deleted.push({
                deliverableUuid,
                deliverableLockVersion,
                sprintUuid: sprint.uuid,
              })
            }
          })
        } else {
          const sprint = wbsItem?.sprint
          if (
            sprint &&
            (!originalSprints ||
              !originalSprints.some(v => v.uuid === sprint.uuid))
          ) {
            sprintProductRequest.added.push({
              deliverableUuid,
              deliverableLockVersion,
              sprintUuid: sprint.uuid,
            })
          }

          originalSprints?.forEach(v => {
            if (v.uuid === sprint?.uuid) return
            sprintProductRequest.deleted.push({
              deliverableUuid,
              deliverableLockVersion,
              sprintUuid: v.uuid,
            })
          })
        }
      })
      if (
        _.isEmpty(sprintProductRequest.added) &&
        _.isEmpty(sprintProductRequest.deleted)
      ) {
        return successDummyResponse
      }
      const response = await updateSprintProductItems(sprintProductRequest)
      return response
    },
    []
  )

  const updateSprintBacklog = useCallback(
    async (
      data: WbsItemSearchRow[],
      originalDataMap: Map<string, WbsItemSearchRow>,
      wbsItemLockVersionMap: { [wbsItemUuid: string]: number }
    ) => {
      const useSprintsMultiSelect = featureFlagService.isEnabled(
        'sprints-multi-select'
      )
      const tasks = data.filter(
        (row: WbsItemSearchRow) =>
          row.edited &&
          !!row.wbsItem?.uuid &&
          row.wbsItem?.wbsItemType?.isTask()
      )
      if (_.isEmpty(tasks)) return successDummyResponse
      const sprintBacklogRequest: UpdateBatchSprintBacklogProps = {
        sprintBacklogItems: [],
        sprintPlans: {
          added: [],
          deleted: [],
        },
      }
      tasks.forEach(row => {
        const wbsItem = row.wbsItem
        const taskUuid = wbsItem.uuid!
        const taskLockVersion =
          wbsItemLockVersionMap[taskUuid] ?? wbsItem.lockVersion!

        if (useSprintsMultiSelect) {
          const currentSprints = wbsItem.sprints ?? []
          const originalSprints =
            originalDataMap.get(row.uuid)?.wbsItem.sprints ?? []
          const currentSprintUuids = new Set(currentSprints.map(s => s.uuid))
          const originalSprintUuids = new Set(originalSprints.map(s => s.uuid))

          currentSprints.forEach(sprint => {
            if (!originalSprintUuids.has(sprint.uuid)) {
              sprintBacklogRequest.sprintPlans.added.push({
                taskUuid,
                taskLockVersion,
                sprintUuid: sprint.uuid,
              })
            }
          })

          originalSprints.forEach(sprint => {
            if (!currentSprintUuids.has(sprint.uuid)) {
              sprintBacklogRequest.sprintPlans.deleted.push({
                taskUuid,
                taskLockVersion,
                sprintUuid: sprint.uuid,
              })
            }
          })
        } else {
          const sprint = wbsItem?.sprint
          const originalSprint = originalDataMap.get(row.uuid)?.wbsItem.sprint
          if (
            !!sprint?.uuid &&
            (row.added ||
              (row.edited &&
                (!originalSprint || originalSprint.uuid !== sprint.uuid)))
          ) {
            sprintBacklogRequest.sprintPlans.added.push({
              taskUuid,
              taskLockVersion,
              sprintUuid: sprint.uuid,
            })
          } else if (row.edited && !sprint?.uuid && !!originalSprint) {
            sprintBacklogRequest.sprintPlans.deleted.push({
              taskUuid,
              taskLockVersion,
              sprintUuid: originalSprint.uuid,
            })
          }
        }
      })
      if (
        _.isEmpty(sprintBacklogRequest.sprintPlans.added) &&
        _.isEmpty(sprintBacklogRequest.sprintPlans.deleted)
      ) {
        return successDummyResponse
      }
      return SprintBacklogApi.updateBatchSprintBacklog(sprintBacklogRequest)
    },
    []
  )

  const saveWbsItem = useCallback(
    async (saveData: WbsItemSearchRow[]) => {
      const useSprintsMultiSelect = featureFlagService.isEnabled(
        'sprints-multi-select'
      )
      if (useSprintsMultiSelect) {
        saveData.forEach(row => {
          const wbsItem = row.wbsItem
          if (!wbsItem) return
          if (
            !wbsItem.wbsItemType?.isTask() &&
            !wbsItem.wbsItemType?.isDeliverable()
          ) {
            return
          }
          syncSprintFromSprints(wbsItem)
        })
      }

      const updateWbsItemResponse = await updateWbsItem(
        projectUuid,
        saveData,
        originalData.current
      )
      const wbsItemLockVersionMap = updateWbsItemResponse?.json.edited
        ? Object.fromEntries(
            [...updateWbsItemResponse?.json.edited].map(v => [
              v.uuid,
              v.lockVersion,
            ])
          )
        : {}
      const updateSprintProductResponse = await updateSprintProduct(
        saveData,
        originalData.current,
        wbsItemLockVersionMap
      )
      const updateSprintBacklogResponse = await updateSprintBacklog(
        saveData,
        originalData.current,
        wbsItemLockVersionMap
      )
      return [
        updateWbsItemResponse,
        updateSprintProductResponse,
        updateSprintBacklogResponse,
      ]
    },
    [projectUuid, updateWbsItem, updateSprintProduct, updateSprintBacklog]
  )

  const save = useCallback(async () => {
    const response = await saveWbsItem(data)
    return response
  }, [data, saveWbsItem])

  const saveSingleRow = useCallback(
    async (uuid: string) => {
      const target = data.filter(row => row.uuid === uuid && row.edited)
      if (!target) return
      await saveWbsItem(target)
    },
    [data, saveWbsItem]
  )

  const generateWbsItemDeltaRequest = useCallback(
    (row: WbsItemSearchRow | undefined): WbsItemDeltaInput | undefined =>
      rowToWbsItemDeltaInput(row, originalData.current),
    [originalData, rowToWbsItemDeltaInput]
  )

  return {
    totalDataSize,
    hitDataSize,
    count,
    data,
    refresh,
    refreshSingleRow,
    save,
    saveSingleRow,
    generateWbsItemDeltaRequest,
  }
}
